import type { Classroom, Student } from "@classroom/domain";
import { useEffect, useMemo, useRef, useState } from "react";
import { i18n, useTranslation } from "./i18n";
import {
  buildHistoryPlan,
  guessMap,
  HISTORY_FIELDS,
  HISTORY_KINDS,
  type HistoryCsvEncoding,
  HistoryImportError,
  type HistoryKind,
  type HistoryMap,
  type HistoryPlan,
  type HistorySheet,
  type HistorySource,
  readHistoryCsv,
  readHistoryText,
  readHistoryWorkbook,
  reviewHistorySheet,
} from "./lib/recordImport";
import StudentPagination, { useStudentPagination } from "./StudentPagination";

type Props = {
  classItem?: Classroom;
  students: Student[];
  source: HistorySource;
  workspaceId: string;
  onSave: (plan: HistoryPlan) => Promise<void>;
  toast: (message: string) => void;
};
const allowedKind = (kind: HistoryKind, role?: Classroom["classRole"]) =>
  role === "teaching"
    ? kind === "materials" || kind === "answers"
    : kind === "attendance" || kind === "missingHomework" || kind === "routine";

export default function RecordImportPanel({ classItem, students, source, workspaceId, onSave, toast }: Props) {
  const { t } = useTranslation("historyImport");
  const [method, setMethod] = useState<"excel" | "csv" | "paste">("excel");
  const [encoding, setEncoding] = useState<HistoryCsvEncoding>("utf-8");
  const [selectedFile, setSelectedFile] = useState<File>();
  const [pasteText, setPasteText] = useState("");
  const readGeneration = useRef(0);
  const [sheets, setSheets] = useState<HistorySheet[]>([]);
  const [date1904, setDate1904] = useState(false);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [kind, setKind] = useState<HistoryKind>("attendance");
  const [headerRow, setHeaderRow] = useState(0);
  const [map, setMap] = useState<HistoryMap>({});
  const [included, setIncluded] = useState<Set<number>>(new Set());
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const sheet = sheets[sheetIndex];
  const headers = sheet?.rows[headerRow] || [];
  const sourceArrays = [
    source.attendanceRecords,
    source.homeroomHomeworkRecords,
    source.dailyRecords,
    source.studentRoutineItems,
  ] as const;

  function setCurrentSheet(nextSheets: HistorySheet[], index: number, role = classItem?.classRole) {
    const next = nextSheets[index];
    if (!next) return;
    const nextKind = allowedKind(next.suggestedKind, role)
      ? next.suggestedKind
      : role === "teaching"
        ? "materials"
        : "attendance";
    setSheetIndex(index);
    setKind(nextKind);
    setHeaderRow(next.suggestedHeader);
    setMap(guessMap(next.rows[next.suggestedHeader] || [], nextKind));
    setAllowOverwrite(false);
    setError("");
    setDone("");
  }

  function clearPreview() {
    readGeneration.current += 1;
    setReading(false);
    setSheets([]);
    setIncluded(new Set());
    setError("");
    setDone("");
  }

  function readError(caught: unknown) {
    return caught instanceof HistoryImportError
      ? t(`errors.${caught.code}`)
      : caught instanceof Error
        ? caught.message
        : t("readFailed");
  }

  async function readFile(file?: File, csvEncoding = encoding) {
    if (!file) return;
    clearPreview();
    const generation = readGeneration.current;
    setSelectedFile(file);
    setReading(true);
    try {
      const result = method === "csv" ? await readHistoryCsv(file, csvEncoding) : await readHistoryWorkbook(file);
      if (generation !== readGeneration.current) return;
      setSheets(result.sheets);
      setDate1904(result.date1904);
      setCurrentSheet(result.sheets, 0);
    } catch (caught) {
      if (generation === readGeneration.current) setError(readError(caught));
    } finally {
      if (generation === readGeneration.current) setReading(false);
    }
  }

  function readPaste() {
    clearPreview();
    try {
      const result = readHistoryText(pasteText, t("pastedSheet"));
      setSheets(result.sheets);
      setDate1904(false);
      setCurrentSheet(result.sheets, 0);
    } catch (caught) {
      setError(readError(caught));
    }
  }

  const review = useMemo(() => {
    if (!sheet || !classItem) return { rows: [], error: "" };
    try {
      return {
        rows: reviewHistorySheet(sheet, kind, headerRow, map, students, source, classItem.id, date1904),
        error: "",
      };
    } catch (caught) {
      return {
        rows: [],
        error:
          caught instanceof Error ? caught.message : i18n.t("screenCopy:recordImportPanel.couldNotAnalyseTheWorksheet"),
      };
    }
    // Use the individual record arrays so a parent rerender does not reset row choices.
  }, [sheet, kind, headerRow, map, students, classItem?.id, date1904, ...sourceArrays]);
  useEffect(() => {
    setIncluded(new Set(review.rows.filter((row) => row.status === "ready").map((row) => row.rowNumber)));
  }, [review.rows]);
  useEffect(() => {
    clearPreview();
    setSelectedFile(undefined);
    setPasteText("");
  }, [classItem?.id]);
  const pagination = useStudentPagination(
    review.rows,
    JSON.stringify([classItem?.id, sheet?.name, sheetIndex, kind, headerRow, map]),
  );
  const ready = review.rows.filter((row) => row.status === "ready").length;
  const duplicate = review.rows.filter((row) => row.status === "duplicate").length;
  const conflict = review.rows.filter((row) => row.status === "conflict").length;
  const invalid = review.rows.filter((row) => row.status === "invalid").length;
  const chosen = review.rows.filter(
    (row) =>
      included.has(row.rowNumber) &&
      (row.status === "ready" || (allowOverwrite && row.status === "conflict" && !/行已有/.test(row.reason))),
  );

  async function importSelected() {
    if (!classItem || !chosen.length || saving) return;
    setSaving(true);
    setError("");
    try {
      const plan = buildHistoryPlan(kind, chosen, source, classItem.id, workspaceId);
      await onSave(plan);
      const message = i18n.t("screenCopy:recordImportPanel.importedRowsInto", {
        length: chosen.length,
        label: HISTORY_KINDS.find((item) => item.value === kind)?.label || kind,
        name: classItem.name,
      });
      setDone(message);
      setSheets([]);
      setIncluded(new Set());
      setSelectedFile(undefined);
      if (method === "paste") setPasteText("");
      toast(message);
    } catch (caught) {
      setError(
        i18n.t("screenCopy:recordImportPanel.couldNotCompleteImportIfSomeData", {
          value: caught instanceof Error ? caught.message : i18n.t("interfaceExtras:unknownError"),
        }),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="paper-card history-import"
      aria-label={t("title")}
      data-save-state={saving ? "saving" : selectedFile || pasteText.trim() || sheets.length ? "dirty" : "saved"}
    >
      {!classItem && <p role="status">{i18n.t("screenCopy:recordImportPanel.chooseAClassFirst")}</p>}
      <fieldset className="history-import-methods" disabled={reading || saving}>
        <legend>{t("method")}</legend>
        {(["excel", "csv", "paste"] as const).map((value) => (
          <label key={value} className={method === value ? "selected" : ""}>
            <input
              type="radio"
              name="history-import-method"
              value={value}
              checked={method === value}
              onChange={() => {
                clearPreview();
                setSelectedFile(undefined);
                setMethod(value);
              }}
            />
            {t(`methods.${value}`)}
          </label>
        ))}
      </fieldset>
      {method === "paste" ? (
        <div className="history-import-paste">
          <label>
            {t("paste")}
            <textarea
              aria-label={t("paste")}
              value={pasteText}
              disabled={!classItem || saving}
              spellCheck={false}
              onChange={(event) => {
                setPasteText(event.target.value);
                clearPreview();
              }}
            />
          </label>
          <button
            type="button"
            className="primary-action"
            disabled={!classItem || !pasteText.trim() || saving}
            onClick={readPaste}
          >
            {t("analyze")}
          </button>
        </div>
      ) : (
        <div className="history-import-inputs">
          <label className="history-import-file">
            {t(`fileLabels.${method}`)}
            <input
              key={`${method}-${classItem?.id}`}
              type="file"
              aria-label={t(`fileLabels.${method}`)}
              accept={
                method === "csv"
                  ? ".csv,text/csv"
                  : ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              }
              disabled={!classItem || reading || saving}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = "";
                void readFile(file);
              }}
            />
            {selectedFile && <span className="history-import-filename">{selectedFile.name}</span>}
          </label>
          {method === "csv" && (
            <label className="history-import-encoding">
              {t("encoding")}
              <select
                aria-label={t("encoding")}
                value={encoding}
                disabled={reading || saving}
                onChange={(event) => {
                  const next = event.target.value as HistoryCsvEncoding;
                  setEncoding(next);
                  if (selectedFile) void readFile(selectedFile, next);
                }}
              >
                <option value="utf-8">{t("encodings.utf8")}</option>
                <option value="big5">{t("encodings.big5")}</option>
              </select>
            </label>
          )}
        </div>
      )}
      {reading && <p role="status">{t("reading")}</p>}
      {done && (
        <p className="history-import-success" role="status">
          {done}
        </p>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {sheet && classItem && (
        <>
          <div className="history-import-settings">
            <label>
              {i18n.t("calendarImport:sheet")}
              <select
                disabled={saving}
                value={sheetIndex}
                onChange={(event) => setCurrentSheet(sheets, Number(event.target.value))}
              >
                {sheets.map((item, index) => (
                  <option key={`${item.name}_${index}`} value={index}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {i18n.t("screenCopy:recordImportPanel.recordType")}
              <select
                disabled={saving}
                value={kind}
                onChange={(event) => {
                  const next = event.target.value as HistoryKind;
                  setKind(next);
                  setMap(guessMap(headers, next));
                  setAllowOverwrite(false);
                }}
              >
                {HISTORY_KINDS.filter((item) => allowedKind(item.value, classItem.classRole)).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {i18n.t("screenCopy:recordImportPanel.headerRow")}
              <select
                disabled={saving}
                value={headerRow}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  setHeaderRow(next);
                  setMap(guessMap(sheet.rows[next] || [], kind));
                }}
              >
                {sheet.rows.slice(0, 15).map((row, index) => (
                  <option key={index} value={index}>
                    {i18n.t("screenCopy:recordImportPanel.row")} {index + 1}{" "}
                    {i18n.t("screenCopy:recordImportPanel.label")}
                    {row.filter(Boolean).slice(0, 3).join("、") || i18n.t("personalTools:blank")}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <fieldset className="history-import-mapping" disabled={saving}>
            <legend>{i18n.t("screenCopy:recordImportPanel.reviewColumns")}</legend>
            {HISTORY_FIELDS[kind].map((field) => (
              <label key={field.value}>
                {field.label}
                {field.required ? " *" : ""}
                <select
                  value={map[field.value] ?? ""}
                  onChange={(event) =>
                    setMap((current) => ({
                      ...current,
                      [field.value]: event.target.value === "" ? undefined : Number(event.target.value),
                    }))
                  }
                >
                  <option value="">{i18n.t("screenCopy:recordImportPanel.doNotUse")}</option>
                  {headers.map((header, index) => (
                    <option key={index} value={index}>
                      {String.fromCharCode(65 + index)} ·{" "}
                      {header || i18n.t("screenCopy:recordImportPanel.untitledColumn", { value: index + 1 })}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </fieldset>
          {review.error ? (
            <p className="form-error" role="alert">
              {review.error}
            </p>
          ) : (
            <>
              <div className="history-import-summary" role="status">
                <b>
                  {i18n.t("screenCopy:recordImportPanel.readyToImport")} {ready}
                </b>
                <span>
                  {i18n.t("screenCopy:recordImportPanel.alreadyExists")} {duplicate}
                </span>
                <span>
                  {i18n.t("screenCopy:recordImportPanel.conflict")} {conflict}
                </span>
                <span>
                  {i18n.t("screenCopy:recordImportPanel.needsCorrection")} {invalid}
                </span>
                <strong>
                  {i18n.t("screenCopy:recordImportPanel.selected")} {chosen.length}
                </strong>
              </div>
              {conflict > 0 && (
                <label className="history-import-overwrite">
                  <input
                    type="checkbox"
                    disabled={saving}
                    checked={allowOverwrite}
                    onChange={(event) => {
                      setAllowOverwrite(event.target.checked);
                      setIncluded(
                        new Set(review.rows.filter((row) => row.status === "ready").map((row) => row.rowNumber)),
                      );
                    }}
                  />
                  {t("overwrite")}
                </label>
              )}
              <div className="history-import-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">{i18n.t("screenCopy:recordImportPanel.import")}</th>
                      <th scope="col">{t("sourceRow")}</th>
                      <th scope="col">{i18n.t("cloudMigration:collections.students")}</th>
                      <th scope="col">{i18n.t("classroomPerformance:students.date")}</th>
                      <th scope="col">{i18n.t("screenCopy:recordImportPanel.record")}</th>
                      <th scope="col">{i18n.t("screenCopy:recordImportPanel.checkResult")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagination.items.map((row) => {
                      const selectable =
                        row.status === "ready" ||
                        (allowOverwrite && row.status === "conflict" && !/行已有/.test(row.reason));
                      return (
                        <tr key={row.rowNumber} className={row.status}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={i18n.t("screenCopy:recordImportPanel.importRow", {
                                rowNumber: row.rowNumber,
                              })}
                              disabled={!selectable || saving}
                              checked={included.has(row.rowNumber) && selectable}
                              onChange={(event) =>
                                setIncluded((current) => {
                                  const next = new Set(current);
                                  if (event.target.checked) next.add(row.rowNumber);
                                  else next.delete(row.rowNumber);
                                  return next;
                                })
                              }
                            />
                          </td>
                          <td>{row.rowNumber}</td>
                          <td>{row.studentLabel}</td>
                          <td>{row.date || "—"}</td>
                          <td>{row.value || "—"}</td>
                          <td>{row.reason}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <StudentPagination {...pagination} disabled={saving} />
              <footer>
                <button type="button" disabled={saving} onClick={clearPreview}>
                  {i18n.t("personalTools:cancel")}
                </button>
                <button
                  type="button"
                  className="primary-action"
                  disabled={!chosen.length || saving}
                  onClick={() => void importSelected()}
                >
                  {saving
                    ? i18n.t("screenCopy:recordImportPanel.importing")
                    : i18n.t("screenCopy:recordImportPanel.confirmImportOfRows", { length: chosen.length })}
                </button>
              </footer>
            </>
          )}
        </>
      )}
    </section>
  );
}
